import React, { type ReactNode } from "react";
import "./shell.css";

import type { T_ChatV8ThreadListItem } from "@lattice/core-lib/types/chat-v8";

import { ShellHeader } from "./ShellHeader";
import { SHELL_HISTORY_RAIL_ID, ThreadHistoryRail } from "./ThreadHistoryRail";

export type T_DesktopShellFrameProps = {
  activeThreadCuid: string | null;
  children: ReactNode;
  historyOpen: boolean;
  /** True only on the new-chat route; drives the New chat button's fill. */
  newChatActive?: boolean;
  onDeleteThread: (threadCuid: string) => Promise<void>;
  onNewChat: () => void;
  onOpenSettings: () => void;
  onRenameThread: (threadCuid: string, name: string) => Promise<void>;
  onSelectThread: (threadCuid: string) => void;
  onToggleHistory: () => void;
  threadTitle: string | null;
  threads: T_ChatV8ThreadListItem[];
};

/** Production desktop page composition. Mobile shares state and kit primitives,
 * but intentionally owns a different native shell layout. */
export function DesktopShellFrame({
  activeThreadCuid,
  children,
  historyOpen,
  newChatActive,
  onDeleteThread,
  onNewChat,
  onOpenSettings,
  onRenameThread,
  onSelectThread,
  onToggleHistory,
  threadTitle,
  threads,
}: T_DesktopShellFrameProps) {
  return (
    <div className="shell-frame">
      <ThreadHistoryRail
        activeThreadCuid={activeThreadCuid}
        newChatActive={newChatActive}
        onDeleteThread={onDeleteThread}
        onNewChat={onNewChat}
        onOpenSettings={onOpenSettings}
        onRenameThread={onRenameThread}
        onSelectThread={onSelectThread}
        open={historyOpen}
        threads={threads}
      />
      <button
        aria-hidden={!historyOpen}
        aria-label="Close conversation history"
        className={[
          "shell-rail-backdrop",
          historyOpen ? "shell-rail-backdrop--open" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        onClick={onToggleHistory}
        tabIndex={historyOpen ? 0 : -1}
        type="button"
      />
      <div className="shell-main">
        <ShellHeader
          historyControlsId={SHELL_HISTORY_RAIL_ID}
          historyOpen={historyOpen}
          onNewChat={onNewChat}
          onToggleHistory={onToggleHistory}
          threadTitle={threadTitle}
        />
        <div className="shell-surface">{children}</div>
      </div>
    </div>
  );
}
